import React from 'react';
import { Check, CheckCircle2 } from 'lucide-react';
import { CompactError, Pill, StepBar } from '../../../components/ui';
import { useReadOnly } from '../../../contexts/WizardAccessContext';
import { EndUserHandoffBanner, SubStepPills } from '../../../components/wizard';
import ContextQuestionList from './ContextQuestionList';
import { AnalysisWaitOverlay, QuestionsSkeleton } from './AnalysisWait';
import { BomAnswer, ContextQuestion, ExactPart, useContextStep } from './useContextStep';
import { plural } from '../../../utils/waitCopy';
import type { EndUserHandoffView } from '../../../hooks/useEndUserHandoff';

/**
 * Step 3a — the context-questions screen from the 3a mock: sub-step pills,
 * the "Robbie needs N answers" card of numbered questions grouped by
 * component — each component closing on a collapsed line of what Robbie read
 * off the BOM instead of asking — and the StepBar. While the analysis runs a
 * Robbie overlay covers the column; a failed run shows in the same card.
 * Presentational + local form state; the hosting step owns every API call.
 *
 * A viewer reads the answers and moves on: no save, only the step-through.
 */
/**
 * Size of the BOM analysis run that gates this step. The run is what
 * produces the context questions, so `completed` reaches `total` exactly when
 * the run lands — there is no per-component percentage to invent in between.
 */
export interface AnalysisProgress {
  completed: number;
  total: number;
}

export interface ContextStepPanelProps {
  /** The questions still open — the BOM did not answer these. */
  questions: ContextQuestion[];
  /** What the BOM already answered; shown pre-filled at the foot of its component. */
  bomAnswers?: BomAnswer[];
  /** Rows that name an exact part, so nothing was asked of them. */
  exactParts?: ExactPart[];
  isAnalyzing: boolean;
  progress: AnalysisProgress;
  onSubmit: (answers: Record<string, any>) => Promise<void>;
  onBack: () => void;
  /** Advance when there is nothing left to answer. */
  onContinue: () => void;
  onSubStepSelect?: (id: string) => void;
  /** End-user handoff view — banner + read-only gating of the answer form. */
  handoff?: EndUserHandoffView;
  /** The analysis run failed; the overlay offers to run it again. */
  analysisFailed?: boolean;
  onRetryAnalysis?: () => void;
  retryError?: string | null;
  /** The analysis is past its usual range. */
  longWait?: boolean;
}

/** One quiet line once the analysis has landed — what Robbie went through. */
const AnalysisDoneLine: React.FC<{ total: number; filled: number }> = ({ total, filled }) => (
  <p className="m-0 flex items-center gap-s3 text-body-sm text-ink-secondary">
    <Check aria-hidden="true" className="h-[14px] w-[14px] shrink-0 text-success-ink" />
    <span>
      Went through <strong className="font-semibold text-ink">{plural(total, 'component')}</strong>
      {filled > 0 && ` · filled ${plural(filled, 'spec')} from your BOM`}
    </span>
  </p>
);

const NO_BOM_ANSWERS: BomAnswer[] = [];
const NO_EXACT_PARTS: ExactPart[] = [];

export const ContextStepPanel: React.FC<ContextStepPanelProps> = ({
  questions,
  bomAnswers = NO_BOM_ANSWERS,
  exactParts = NO_EXACT_PARTS,
  isAnalyzing,
  progress,
  onSubmit,
  onBack,
  onContinue,
  onSubStepSelect,
  handoff,
  analysisFailed = false,
  onRetryAnalysis,
  retryError,
  longWait = false,
}) => {
  // Two read-only sources compose: the role model (viewer) and the handoff
  // (end user outside their window). The open fill window overrides both —
  // the pending end user is technically a VIEWER, yet these questions are
  // exactly what was handed to them to answer.
  const viewerReadOnly = useReadOnly();
  const readOnly = handoff?.canFill ? false : viewerReadOnly || (handoff?.readOnly ?? false);
  const { answers, setAnswer, submit, isSubmitting, error, editedBomAnswer, answeredCount } = useContextStep(
    questions,
    onSubmit,
    bomAnswers,
  );
  const waiting = isAnalyzing || analysisFailed;
  const hasQuestions = questions.length > 0;
  const contextDone = !hasQuestions && !waiting;
  // Something to save: an open question, or a BOM answer the buyer corrected.
  const needsSave = hasQuestions || editedBomAnswer;
  const perComponent = questions.some((q) => q.componentKey);
  const hasBomReads = !waiting && (bomAnswers.length > 0 || exactParts.length > 0);
  const list = (
    <ContextQuestionList
      questions={questions}
      bomAnswers={waiting ? NO_BOM_ANSWERS : bomAnswers}
      exactParts={waiting ? NO_EXACT_PARTS : exactParts}
      answers={answers}
      onAnswer={setAnswer}
      readOnly={readOnly}
    />
  );

  const summary = waiting ? (
    <>
      Your questions appear once I&apos;ve gone through{' '}
      <strong className="font-semibold text-ink">{plural(progress.total, 'component')}</strong>
    </>
  ) : hasQuestions ? (
    <>
      <strong className="font-semibold text-ink">
        {answeredCount} of {questions.length}
      </strong>{' '}
      questions answered
    </>
  ) : (
    'Context complete — nothing left to ask'
  );

  return (
    <div className="relative flex flex-col gap-s5 font-sans">
      <SubStepPills
        items={[
          { id: 'context', label: 'Context', state: 'active' },
          { id: 'validation', label: 'Validation', state: 'upcoming' },
          { id: 'discovery', label: 'Discovery', state: 'upcoming' },
        ]}
        onSelect={onSubStepSelect}
      />

      {handoff && <EndUserHandoffBanner handoff={handoff} />}

      {!waiting && progress.total > 0 && <AnalysisDoneLine total={progress.total} filled={bomAnswers.length} />}

      {waiting ? (
        <QuestionsSkeleton />
      ) : hasQuestions ? (
        <div className="overflow-hidden rounded-card border border-line bg-surface">
          <div className="flex items-center justify-between gap-s4 border-b border-line-divider-soft px-s6 py-[13px]">
            <div>
              <div className="text-card-header text-ink">
                {readOnly
                  ? `Robbie asked ${plural(questions.length, 'question')}`
                  : `Robbie needs ${plural(questions.length, 'answer')}`}
              </div>
              <div className="mt-[2px] text-meta text-ink-muted">
                {perComponent
                  ? "Grouped by component — only what your BOM doesn't already say"
                  : 'They apply to every component in this BOM'}
              </div>
            </div>
            <Pill
              status={answeredCount >= questions.length ? 'success' : 'neutral'}
              className="shrink-0 whitespace-nowrap px-[9px] py-[3px] font-semibold"
            >
              {answeredCount} of {questions.length} answered
            </Pill>
          </div>
          {list}
        </div>
      ) : (
        <>
          <div className="flex items-center gap-s5 rounded-card border border-line bg-surface px-s6 py-[18px]">
            <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-control border border-success-border bg-success-bg">
              <CheckCircle2 className="h-4 w-4 text-success-ink" />
            </span>
            <div>
              <p className="m-0 text-row-title text-ink">Context complete</p>
              <p className="m-0 mt-[2px] text-body-sm text-ink-secondary">
                I have everything I need — next I&apos;ll check this BOM for gaps before finding suppliers.
              </p>
            </div>
          </div>
          {hasBomReads && (
            <div className="overflow-hidden rounded-card border border-line bg-surface">
              <div className="border-b border-line-divider-soft px-s6 py-[13px]">
                <div className="text-card-header text-ink">Read from your BOM</div>
                <div className="mt-[2px] text-meta text-ink-muted">Open a component to check what I filled in.</div>
              </div>
              {list}
            </div>
          )}
        </>
      )}

      {error && <CompactError layout="row" title="I couldn't save your answers" message={error} />}

      <StepBar
        stepLabel="Step 3 · Context"
        summary={summary}
        onBack={onBack}
        cta={
          readOnly
            ? { label: hasQuestions ? 'View validation' : 'Continue to validation', onClick: onContinue }
            : {
                label: 'Continue to validation',
                onClick: needsSave ? submit : onContinue,
                disabled: waiting || (!needsSave && !contextDone),
                loading: isSubmitting,
                loadingLabel: 'Saving answers…',
              }
        }
      />

      <AnalysisWaitOverlay
        running={isAnalyzing && !analysisFailed}
        failed={analysisFailed}
        components={progress.total}
        long={longWait}
        onRetry={onRetryAnalysis}
        retryError={retryError}
      />
    </div>
  );
};

export default ContextStepPanel;
